import { useWeb3React } from "@web3-react/core";
import { useLocalStorage, useUpdate } from "react-use";
import { BigNumber, ethers } from "ethers";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NFTAddr, serverPath } from "../config";

import { SofaNFT } from "./contract/SofaNFT";
import { MerkleRet, STAGES, int } from "../types";
import showAlert from "../components/alert";
import { formatError } from "../utils";

export function useRevealContract(queryTick: number = 3000) {
  const { active, error, account, library, deactivate, connector } =
    useWeb3React();
  const update = useUpdate();
  const provider = useRef<ethers.providers.Web3Provider | null>(null);
  const statusRef = useRef<SofaNFT.ContractDashboardStructOutput>();

  const contract = useRef<SofaNFT | null>(null);

  const [loading, setLoading] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const [userTokens, setUserTokens] = useState<int[]>([]);
  const [selectedIds, setSelectedIds] = useState<int[]>([]);

  useEffect(() => {
    if (active) {
      const p = library as ethers.providers.Web3Provider;
      provider.current = p;

      contract.current = new ethers.Contract(
        NFTAddr,
        require("../web3/contract/SofaNFT.json").abi,
        p
      ).connect(p.getSigner()) as SofaNFT;

      queryData();
      //   publicMint(5);
    }
  }, [active]);

  useEffect(() => {
    const interval = setInterval(() => {
      queryData();
    }, queryTick);
    return () => {
      clearInterval(interval);
    };
  }, [active, account]);

  const addressShortcut = useMemo(() => {
    return account == null
      ? "not connected"
      : account.slice(0, 4) + "..." + account.slice(-4);
  }, [account]);

  async function queryData() {
    // console.log("query");
    if (active && contract.current) {
      const tokenIds = await contract.current.tokensOfOwner(account!!);
      setUserTokens(
        tokenIds.map((x) => BigNumber.from(x).toNumber()).filter((x) => x > 1)
      );
    }
  }

  function toggleSelectIds(id: int) {
    const index = selectedIds.indexOf(id);
    if (index === -1) {
      const newIdList = [...selectedIds, id];
      setSelectedIds(newIdList);
    } else {
      setSelectedIds(selectedIds.filter((x) => x !== id));
    }
  }

  async function doReveal(ids: int[]) {
    if (ids.length === 0) {
      throw Error("u have to pick at least 1 sofa to reveal!");
    }
    const list = [...ids];
    try {
      const tx = await contract.current?.reveal(list);
      await tx?.wait();
    } catch (e) {
      throw e;
    }
  }

  function runFunc(callback: () => Promise<any>) {
    return async () => {
      if (loading) return;
      setLoading(true);
      const { hide: hideLoadingAlert } = showAlert({
        title: "Revealing",
        content: <div className="global-loading-content"></div>,
        customCls: "no-corner-img",
      });
      try {
        await callback();
        showAlert({
          success: true,
          title: "Reveal Success!",
          content: "",
        });
      } catch (e) {
        showAlert({
          title: "Reveal failed",
          content: formatError(e).message,
          success: false,
        });
      } finally {
        setLoading(false);
        hideLoadingAlert();
      }
    };
  }

  const reveal = runFunc(async () => {
    await doReveal(selectedIds);
  });

  const revealAll = runFunc(async () => {
    await doReveal(userTokens);
  });

  return {
    account,
    addressShortcut,
    active,
    contract,
    loading,
    interacting,
    userTokens,
    toggleSelectIds,
    selectedIds,
    reveal,
    revealAll,
  };
}
